import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, type ListBody, type S } from "@/shared/api/client";
import { errorMessage } from "@/shared/i18n";

/** The branches the user can see; branch choices appear only once there is one. */
export function useBranches(enabled = true) {
  return useQuery({ queryKey: ["/v1/core/branches", "all"], queryFn: () => api.get<ListBody<S["Branch"]>>("/v1/core/branches", { limit: 200 }), staleTime: 60_000, enabled });
}

export interface ListParams {
  q?: string;
  sort?: string;
  filter?: Record<string, string>;
  limit?: number;
}

/** Cursor-paginated list for crud endpoints. */
export function useList<T>(path: string, params: ListParams = {}, enabled = true) {
  const q = useInfiniteQuery({
    queryKey: [path, "list", params],
    queryFn: ({ pageParam }) =>
      api.get<ListBody<T>>(path, { q: params.q, sort: params.sort, filter: params.filter, limit: params.limit ?? 50, cursor: pageParam }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor || undefined,
    // A new search or filter keeps the rows on screen until its own arrive, rather than blanking the table.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[0] === path ? prev : undefined),
    enabled,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;
  return { ...q, items, total };
}

/** How many rows a list has under a filter, fetching one: the number beside a list's tab. Refreshed with the list. */
export function useCount(path: string, filter: Record<string, string>, enabled = true): number | undefined {
  return useQuery({ queryKey: [path, "count", filter], queryFn: () => api.get<ListBody<unknown>>(path, { filter, limit: 1 }), select: (d) => d.total, enabled }).data;
}

/**
 * Refreshes the queries under a key. An API path alone ("/v1/inventory") covers the
 * paths below it too (["/v1/inventory/purchase-orders", id]): React Query matches key
 * segments exactly, so without this a change refreshed nothing below the path.
 */
export function invalidate(qc: QueryClient, key: QueryKey) {
  const p = key[0];
  if (key.length === 1 && typeof p === "string" && p.startsWith("/")) {
    return qc.invalidateQueries({ predicate: (x) => typeof x.queryKey[0] === "string" && (x.queryKey[0] === p || x.queryKey[0].startsWith(p + "/")) });
  }
  return qc.invalidateQueries({ queryKey: key });
}

/** Mutation that toasts errors and invalidates keys (by prefix) on success. */
export function useAction<TVars, TOut>(fn: (v: TVars) => Promise<TOut>, opts: { invalidate?: QueryKey[]; success?: string; onSuccess?: (out: TOut, v: TVars) => void } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (out, v) => {
      for (const k of opts.invalidate ?? []) await invalidate(qc, k);
      if (opts.success) toast.success(opts.success);
      opts.onSuccess?.(out, v);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}
